/**
 * Confirmation Policy — formal HR policy document (employee-facing wording),
 * version control helpers, and print.
 */

import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";

/** @param {string} ymd */
function parseLocalYMD(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const [y, m, d] = ymd.split("-").map((n) => Number(n));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * Auto-increment version when the effective date moves forward to a new calendar date
 * that is strictly after the previous effective date and strictly after today (future-only).
 * @param {object|null} prevPolicy
 * @param {object} payload
 * @param {boolean} isNew
 */
export function computeVersionOnSave(prevPolicy, payload, isNew) {
  const now = new Date().toISOString();
  const newEff = payload.effectiveDate;
  const policyName = payload.policyName || "";

  if (isNew) {
    return {
      policyVersion: 1,
      policyVersionHistory: [
        {
          version: 1,
          effectiveDate: newEff,
          savedAt: now,
          event: "created",
          policyName,
        },
      ],
      versionBumped: false,
    };
  }

  const prevEff = prevPolicy.effectiveDate;
  const prevVer = prevPolicy.policyVersion || 1;
  const history = Array.isArray(prevPolicy.policyVersionHistory)
    ? [...prevPolicy.policyVersionHistory]
    : [];

  let nextVer = prevVer;
  let bumped = false;

  if (newEff && prevEff && newEff !== prevEff) {
    const dNew = parseLocalYMD(newEff);
    const dPrev = parseLocalYMD(prevEff);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dNew && dPrev && dNew > dPrev && dNew > today) {
      nextVer = prevVer + 1;
      bumped = true;
    }
  }

  history.push({
    version: nextVer,
    effectiveDate: newEff,
    savedAt: now,
    event: bumped ? "version_increment" : "updated",
    policyName,
  });

  const trimmed = history.slice(-100);

  return {
    policyVersion: nextVer,
    policyVersionHistory: trimmed,
    versionBumped: bumped,
  };
}

/** Deep clone for snapshot storage (plain JSON policy objects only). */
export function clonePolicySnapshot(policy) {
  try {
    const o = { ...(policy || {}) };
    delete o.policySnapshots;
    delete o.policyVersionHistory;
    return JSON.parse(JSON.stringify(o));
  } catch {
    return { ...policy };
  }
}

/**
 * Build / update policySnapshots[] after save.
 * - On create: one snapshot for version 1.
 * - On version bump: freeze prior version, add new version snapshot.
 * - On same-version edit: replace snapshot for that version.
 * @param {object|null} prevPolicy — row before save (editing) or null (create)
 * @param {object} mergedPolicy — full policy row after merge
 * @param {{ policyVersion: number, versionBumped: boolean }} versionResult
 * @param {boolean} isNew
 * @param {string} savedAtIso
 */
/** Sorted newest version first; falls back to current policy as a single row. */
export function getPolicySnapshotsForDisplay(policy) {
  const p = policy || {};
  if (Array.isArray(p.policySnapshots) && p.policySnapshots.length > 0) {
    return [...p.policySnapshots].sort((a, b) => b.version - a.version);
  }
  return [
    {
      version: p.policyVersion || 1,
      effectiveDate: p.effectiveDate || "",
      savedAt: p.updatedAt || p.createdAt || new Date().toISOString(),
      policy: clonePolicySnapshot(p),
    },
  ];
}

export function mergePolicySnapshots(prevPolicy, mergedPolicy, versionResult, isNew, savedAtIso) {
  const ver = mergedPolicy.policyVersion || 1;
  const eff = mergedPolicy.effectiveDate || "";

  if (isNew) {
    return [
      {
        version: ver,
        effectiveDate: eff,
        savedAt: savedAtIso,
        policy: clonePolicySnapshot(mergedPolicy),
      },
    ];
  }

  let snaps = Array.isArray(prevPolicy.policySnapshots)
    ? prevPolicy.policySnapshots.map((s) => ({
        ...s,
        policy: s.policy ? clonePolicySnapshot(s.policy) : s.policy,
      }))
    : [];

  if (versionResult.versionBumped) {
    const oldVer = prevPolicy.policyVersion || 1;
    if (!snaps.some((s) => s.version === oldVer)) {
      snaps.push({
        version: oldVer,
        effectiveDate: prevPolicy.effectiveDate || "",
        savedAt: prevPolicy.updatedAt || prevPolicy.createdAt || savedAtIso,
        policy: clonePolicySnapshot(prevPolicy),
      });
    }
  }

  snaps = snaps.filter((s) => s.version !== ver);
  snaps.push({
    version: ver,
    effectiveDate: eff,
    savedAt: savedAtIso,
    policy: clonePolicySnapshot(mergedPolicy),
  });

  snaps.sort((a, b) => a.version - b.version);
  if (snaps.length > 100) snaps = snaps.slice(-100);
  return snaps;
}

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function yn(v) {
  if (v === true || v === "yes") return true;
  if (v === false || v === "no") return false;
  return null;
}

function masterName(list, id) {
  if (!id) return null;
  const f = (list || []).find((x) => String(x.id) === String(id));
  return f?.name || null;
}

/**
 * @param {object} policy
 * @param {object} options
 */
export function buildApplicabilityProse(policy, options) {
  const org = escapeHtml(options.companyName || MASTERS_DOCUMENT_COMPANY_NAME);
  const p = policy || {};

  if (p.allEmployees) {
    const empOpts = options.employmentTypeOptions || [];
    const empTypes = Array.isArray(p.employmentTypes) ? p.employmentTypes : [];
    const empTypeLabels = empTypes.length
      ? empTypes.map((v) => empOpts.find((o) => o.value === v)?.label || v).filter(Boolean)
      : [];
    if (empTypeLabels.length) {
      return `<p>This policy applies to <strong>all employees</strong> of <strong>${org}</strong> whose employment type is one of: <strong>${empTypeLabels
        .map((x) => escapeHtml(x))
        .join(", ")}</strong> (for example full-time, contract, or intern — as reflected in HR records). Other employment types are not covered by this policy unless a separate policy applies.</p>
        <p>If you are unsure of your employment type, check your HR record or contact HR at <strong>${org}</strong>.</p>`;
    }
    return `<p>This policy applies to <strong>all employees</strong> of <strong>${org}</strong> across employment types, unless an individual exception is expressly documented by HR.</p>`;
  }

  const parts = [];
  const b = masterName(options.branchesList, p.branch);
  const d = masterName(options.departmentsList, p.department);
  const des = masterName(options.designationsList, p.designation);
  const g = masterName(options.gradesList, p.grade);
  const c = masterName(options.categoriesList, p.category);
  const sd = masterName(options.subDepartmentsList, p.subDepartment);
  const loc = (options.locationOptions || []).find((o) => o.value === (p.location || ""))?.label;
  const ind = String(p.individualEmployeeIds || "").trim();
  const empOpts = options.employmentTypeOptions || [];
  const empTypes = Array.isArray(p.employmentTypes) ? p.employmentTypes : [];
  const empTypeLabels = empTypes.length
    ? empTypes
        .map((v) => empOpts.find((o) => o.value === v)?.label || v)
        .filter(Boolean)
        .join(", ")
    : "";

  parts.push(
    `<p>The rules set out in this document apply to employees of <strong>${org}</strong> who fall within the coverage described below. Assignment follows the criteria below and is administered by HR.</p>`
  );

  const criteria = [];
  if (b) criteria.push(`<strong>Branch / unit:</strong> ${escapeHtml(b)}`);
  if (d) criteria.push(`<strong>Department:</strong> ${escapeHtml(d)}`);
  if (des) criteria.push(`<strong>Designation / role family:</strong> ${escapeHtml(des)}`);
  if (g) criteria.push(`<strong>Grade / level:</strong> ${escapeHtml(g)}`);
  if (c) criteria.push(`<strong>Employee category:</strong> ${escapeHtml(c)}`);
  if (sd) criteria.push(`<strong>Sub-department / division:</strong> ${escapeHtml(sd)}`);
  if (loc && loc !== "All Locations") criteria.push(`<strong>Location:</strong> ${escapeHtml(loc)}`);
  if (empTypeLabels) {
    criteria.push(
      `<strong>Employment type(s):</strong> ${escapeHtml(empTypeLabels)} (e.g. full-time, contract, intern — as recorded in HR)`
    );
  }

  if (criteria.length) {
    parts.push(`<p>Where the following filters are set, the policy covers employees who match <em>all</em> of the applicable selections:</p><ul>${criteria.map((x) => `<li>${x}</li>`).join("")}</ul>`);
  } else {
    parts.push(
      `<p>No specific branch, department, or other structural filters are set out above for this policy. Coverage follows the standard assignment practice established by HR for <strong>${org}</strong>.</p>`
    );
  }

  if (ind) {
    parts.push(
      `<p><strong>Named individuals:</strong> The policy may also be explicitly assigned to specific employee records identified as follows: ${escapeHtml(ind)}. Where an individual is named, those instructions take precedence together with the criteria above.</p>`
    );
  }

  parts.push(
    `<p>If you are unsure whether this policy applies to your role, please contact HR at <strong>${org}</strong>.</p>`
  );

  return parts.join("");
}

function letterLabel(opts, value) {
  if (!value) return "As per HR template selection";
  const o = (opts || []).find((x) => x.value === value);
  return o?.label || value;
}

/** Hierarchical section numbers: 1, 2, 2.1, 5.1, 5.1.1, 5.1.1.1, … */
function createDocNumberer() {
  return {
    m: 0,
    s: 0,
    ss: 0,
    sss: 0,
    major() {
      this.m++;
      this.s = 0;
      this.ss = 0;
      this.sss = 0;
      return String(this.m);
    },
    sub() {
      this.s++;
      this.ss = 0;
      this.sss = 0;
      return `${this.m}.${this.s}`;
    },
    subsub() {
      this.ss++;
      this.sss = 0;
      return `${this.m}.${this.s}.${this.ss}`;
    },
    subsubsub() {
      this.sss++;
      return `${this.m}.${this.s}.${this.ss}.${this.sss}`;
    },
  };
}

/**
 * @param {object} policy
 * @param {{
 *   companyName?: string,
 *   companyAddress?: string,
 *   preparedBy?: string,
 *   approvedBy?: string,
 *   workflowRoleLabel?: (role: string) => string,
 *   branchesList?: Array<{id:any,name:string}>,
 *   departmentsList?: Array<{id:any,name:string}>,
 *   designationsList?: Array<{id:any,name:string}>,
 *   gradesList?: Array<{id:any,name:string}>,
 *   categoriesList?: Array<{id:any,name:string}>,
 *   subDepartmentsList?: Array<{id:any,name:string}>,
 *   locationOptions?: Array<{value:string,label:string}>,
 *   letterFormatOptions?: Array<{value:string,label:string}>,
 *   probationPeriodOptions?: Array<{value:string,label:string}>,
 *   startDateLogicOptions?: Array<{value:string,label:string}>,
 * }} options
 */
export function buildConfirmationPolicyDocumentHtml(policy, options = {}) {
  const p = policy || {};
  const org = options.companyName || MASTERS_DOCUMENT_COMPANY_NAME;
  const companyName = escapeHtml(org);
  const companyAddress = options.companyAddress ?? MASTERS_DOCUMENT_COMPANY_ADDRESS;
  const preparedBy = options.preparedBy || "Human Resources";
  const approvedBy = options.approvedBy || "Management";
  const roleLabel =
    options.workflowRoleLabel ||
    ((role) => {
      const map = {
        manager: "Manager",
        hr: "HR",
        management: "Management",
        reporting_manager: "Reporting Manager",
      };
      return map[role] || role || "—";
    });

  const policyTitle = escapeHtml(p.policyName || p.name || "HR Confirmation Policy");
  const ver = p.policyVersion || 1;
  const effective = escapeHtml(p.effectiveDate || "—");
  const pushedNote =
    p.policyDocumentPushed && p.policyPushedAt
      ? `<p class="meta"><strong>Employee publication:</strong> This version was published to covered employees on ${escapeHtml(
          new Date(p.policyPushedAt).toLocaleString()
        )}.</p>`
      : "";

  const wf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
  const extWf = Array.isArray(p.extensionWorkflowSteps) ? p.extensionWorkflowSteps : [];
  const termWf = Array.isArray(p.terminationWorkflowSteps) ? p.terminationWorkflowSteps : [];
  const cancelWf = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : [];
  const checklist = Array.isArray(p.checklistMappings) ? p.checklistMappings : [];

  const probLabel =
    p.initialProbationPeriod === "custom"
      ? `${p.customProbationDays ?? 90} calendar days (custom length)`
      : (options.probationPeriodOptions || []).find((o) => o.value === p.initialProbationPeriod)?.label ||
        "the standard probation length defined in this policy";

  const startLogic =
    (options.startDateLogicOptions || []).find((o) => o.value === p.startDateLogic)?.label ||
    (p.startDateLogic === "doj_based"
      ? "from the employee’s date of joining"
      : "as per the custom start rule specified in this policy");

  const approvalMethodProse =
    p.approvalMethod === "workflow"
      ? "Confirmation and related decisions follow the multi-step approval workflow set out in this policy. Each step must be completed within the service levels described below."
      : p.approvalMethod === "on_instance"
      ? "Approvals may be initiated on a case-by-case basis. Where workflow steps apply to an individual case, the same step order and timelines apply."
      : "Eligible cases may be marked as approved automatically when the rules defined in this policy are satisfied, without a manual approval chain. HR may still review exceptions.";

  const reminderList =
    Array.isArray(p.reminderTriggerDays) && p.reminderTriggerDays.length
      ? p.reminderTriggerDays.join(", ")
      : String(p.reminderTriggerDay ?? "");

  const docNum = createDocNumberer();

  const wfStepsHtml = (steps, titleWithoutNumber) => {
    const sec = docNum.sub();
    const fullTitle = `${sec} ${titleWithoutNumber}`;
    if (!steps.length) {
      return `<h3>${escapeHtml(fullTitle)}</h3><p>For this path, no separate approval steps are defined in the current version of this policy. HR will apply the general approval approach described above and exercise judgment in line with company practice.</p>`;
    }
    const items = steps
      .map((s) => {
        const stepNum = docNum.subsub();
        const detailNum = docNum.subsubsub();
        const r = roleLabel(s.role);
        const line = s.notifyOnly
          ? `This step is for <strong>notification only</strong> (no approval action required from ${escapeHtml(r)}).`
          : `${escapeHtml(r)} must complete the approval action within <strong>${s.slaDays ?? 3} calendar day(s)</strong> unless escalated.`;
        return `<li><p><strong>${stepNum} — ${escapeHtml(r)}.</strong></p><p class="step-detail"><strong>${detailNum}</strong> ${line}</p></li>`;
      })
      .join("");
    return `<h3>${escapeHtml(fullTitle)}</h3><ol class="steps">${items}</ol>`;
  };

  const renderChecklistSection = () => {
    const sec = docNum.major();
    if (checklist.length) {
      const rows = checklist
        .map((item) => {
          const rowNum = docNum.sub();
          const trig =
            {
              confirmation: "Upon confirmation approval",
              extension: "Upon extension approval",
              termination: "Upon termination approval",
            }[item.triggerEvent] || item.triggerEvent || "—";
          const dept = item.department ? escapeHtml(item.department) : "—";
          const due =
            item.dueDateLogic === "approval_date"
              ? "from the approval date"
              : item.dueDateLogic === "fixed_days"
              ? `within ${item.dueDateDays ?? 7} day(s) from trigger`
              : escapeHtml(item.dueDateLogic || "—");
          return `<tr>
          <td>${rowNum}</td>
          <td>${escapeHtml(trig)}</td>
          <td>${dept}</td>
          <td>${escapeHtml(item.name || "—")}</td>
          <td>${item.mandatory ? "Mandatory" : "Optional"}</td>
          <td>${due}</td>
        </tr>`;
        })
        .join("");
      return `
      <h2>${sec}. Checklist and compliance tasks</h2>
      <p>The following tasks may be assigned to teams when the stated event occurs. Employees and managers should complete mandatory items on time so that confirmation, extension, or separation processes are not delayed.</p>
      <table class="grid">
        <thead>
          <tr><th>Ref.</th><th>When</th><th>Department</th><th>Task</th><th>Nature</th><th>Due rule</th></tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
    }
    return `<h2>${sec}. Checklist and compliance tasks</h2><p>No checklist tasks are listed in this version of the policy. HR may still assign tasks under general procedures as the need arises.</p>`;
  };

  const addressBlock = companyAddress
    ? `<p class="addr">${escapeHtml(companyAddress).replace(/\n/g, "<br/>")}</p>`
    : "";

  const extAllowed = p.extensionAllowed === "yes";
  const termAllowed = p.terminationDuringProbationAllowed === "yes";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${policyTitle} — ${companyName}</title>
  <style>
    body { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12.5px; line-height: 1.6; color: #1e293b; max-width: 820px; margin: 0 auto; padding: 32px; }
    .letterhead { border-bottom: 2px solid #0f172a; padding-bottom: 18px; margin-bottom: 24px; }
    .company { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 8px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .doc-title { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 15px; font-weight: 700; color: #0f172a; margin: 16px 0 8px 0; text-transform: uppercase; letter-spacing: .06em; }
    .addr { margin: 0; color: #475569; font-size: 11px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .control { margin-top: 14px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 11px; color: #334155; }
    .control strong { color: #0f172a; }
    p.meta { font-size: 11px; color: #64748b; margin-top: 10px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    h2 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 13px; margin: 22px 0 10px 0; color: #0f172a; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    h3 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12px; margin: 14px 0 8px 0; color: #334155; }
    p { margin: 0 0 12px 0; text-align: justify; }
    ul { margin: 8px 0 12px 20px; }
    li { margin-bottom: 6px; }
    ol.steps { margin: 8px 0 16px 0; padding: 0; list-style-type: none; }
    ol.steps li { margin-bottom: 10px; }
    table.grid { width: 100%; border-collapse: collapse; font-size: 11px; margin: 10px 0 16px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    table.grid th, table.grid td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; vertical-align: top; }
    table.grid th { background: #f1f5f9; font-weight: 600; color: #334155; }
    .sign { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 28px; font-size: 11px; color: #475569; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .sign .line { border-top: 1px solid #94a3b8; padding-top: 8px; margin-top: 36px; color: #0f172a; font-weight: 600; }
    @media print { body { padding: 18px; } }
  </style>
</head>
<body>
  <div class="letterhead">
    <p class="company">${companyName}</p>
    ${addressBlock}
    <p class="doc-title">HR confirmation policy</p>
    <p style='font-family:"Segoe UI",Inter,Arial,sans-serif;font-size:13px;margin:0 0 4px 0;'><strong>${policyTitle}</strong></p>
    <div class="control">
      <strong>Document version:</strong> ${escapeHtml(String(ver))} &nbsp;|&nbsp;
      <strong>Effective from:</strong> ${effective} &nbsp;|&nbsp;
      <strong>Owned by:</strong> ${escapeHtml(preparedBy)} / ${escapeHtml(approvedBy)}
    </div>
    ${pushedNote}
  </div>

  <h2>${docNum.major()}. Purpose</h2>
  <p><strong>${docNum.sub()}.</strong> This policy explains how <strong>${companyName}</strong> governs the probationary period, the decision to confirm continued employment, the extension of probation where the organization allows it, the handling of rejection or non-confirmation outcomes, and separation during probation where permitted. It is written in plain language so that every employee who falls within its scope can understand timelines, responsibilities, approvals, communications, and what happens at each stage.</p>
  <p><strong>${docNum.sub()}.</strong> The policy operates together with your employment contract, applicable law, and other HR policies of <strong>${companyName}</strong>. Where this document refers to reminders, workflows, or letters, the arrangements approved by HR and reflected in employee-facing processes apply; this text describes the intended employee experience.</p>

  <h2>${docNum.major()}. Applicability (who this policy covers)</h2>
  ${buildApplicabilityProse(p, { ...options, companyName: org })}
  <h3>${docNum.sub()}. What employees are expected to do</h3>
  <p>If this policy applies to you, you are expected to: attend scheduled review meetings; respond promptly to HR and your manager on probation, confirmation, or extension matters; complete any tasks assigned to you in the checklist (including submissions to IT, Admin, or Finance where relevant); and provide accurate information so that <strong>${companyName}</strong> can make fair and timely decisions. Failure to cooperate may delay confirmation or extension without prejudice to other rights and processes.</p>

  <h2>${docNum.major()}. Definitions</h2>
  <p><strong>${docNum.sub()}. Probation</strong> means the initial assessment window measured from the employee’s date of joining (or from the “start date” rule selected for this policy—e.g. date-of-join-based or a custom start date specified herein). During probation, <strong>${companyName}</strong> evaluates whether the role is a good mutual fit against performance expectations, conduct, and attendance.</p>
  <p><strong>${docNum.sub()}. Confirmation</strong> means a formal outcome that the employee’s employment continues beyond probation under the terms and role communicated, subject to the employment contract and other policies. Until confirmation is recorded (or an exception approved), the employee should treat probation expectations as ongoing.</p>
  <p><strong>${docNum.sub()}. Extension of probation</strong> means a deliberate extension of the probation end date for a defined period, only where this policy allows extensions and the required approvals and documentation are completed.</p>
  <p><strong>${docNum.sub()}. Rejection / non-confirmation of the request</strong> means the decision not to confirm the employee at that stage; the process may allow resubmission or closure, as set out later in this policy.</p>
  <p><strong>${docNum.sub()}. Termination during probation</strong> means separation while the employee is still in probation, only where permitted by this policy, approved through the stated workflow, and carried out in line with notice and settlement rules.</p>

  <h2>${docNum.major()}. Probation period and confirmation timing</h2>
  <p><strong>${docNum.sub()}.</strong> Unless a different rule is expressly approved by HR, the standard probation length under this policy is based on <strong>${escapeHtml(probLabel)}</strong>. The probation timeline is calculated <strong>${escapeHtml(startLogic)}</strong>.</p>
  <p><strong>${docNum.sub()}.</strong> An employee is generally expected to complete at least <strong>${escapeHtml(
    String(p.minServiceDaysForConfirmation ?? 90)
  )} day(s)</strong> of service before a confirmation decision is finalized, so that review is based on adequate tenure.</p>
  <p><strong>${docNum.sub()}.</strong> ${
    yn(p.autoConfirmation === "yes")
      ? `Confirmation may be treated as <strong>automatically satisfied</strong> when the criteria defined in this policy are met. Employees will still receive communications as described under letters below.`
      : `Confirmation is <strong>not</strong> treated as automatic solely by lapse of time; an explicit decision or approval outcome is required.`
  }</p>
  <p><strong>${docNum.sub()}.</strong> HR and the employee’s manager may receive reminders at <strong>${escapeHtml(reminderList || "—")}</strong> calendar day(s) before the expected completion of probation (counted backward from the target completion date), so that reviews, documentation, and decisions can be planned without last-minute rush.</p>
  <p><strong>${docNum.sub()}.</strong> For planning and reporting, an overall probation duration of <strong>${escapeHtml(
    String(p.probationDays ?? 90)
  )}</strong> day(s) is used alongside the initial probation rule and start-date logic above.</p>
  <p><strong>${docNum.sub()}.</strong> ${
    p.autoAssignOnJoin
      ? `<strong>${companyName}</strong> may <strong>assign this policy to new joiners</strong> when they become eligible, so that the correct timelines, reminders, and approvals apply from day one where the applicability rules match.`
      : `Assignment of this policy to an employee is typically done <strong>by HR</strong> when the employee becomes eligible under the applicability rules.`
  }</p>
  <p><strong>${docNum.sub()}.</strong> ${
    yn(p.performanceReviewMandatoryBeforeConfirmation === "yes")
      ? `A <strong>formal performance review</strong> must be completed before a confirmation decision is recorded.`
      : `A formal performance review before confirmation is encouraged as per manager and HR practice, unless a stricter rule is added elsewhere.`
  }</p>

  <h2>${docNum.major()}. How decisions are approved</h2>
  <p>${approvalMethodProse}</p>
  <p>${
    p.approvalMethod === "on_instance" && p.needWorkflowSteps
      ? `For cases where workflow steps apply to an individual instance, the same step order and service levels below apply to that case even though initiation is not from a standing “always-on” workflow.`
      : ""
  }</p>
  <p>${
    p.approvalMethod === "workflow" || p.needWorkflowSteps
      ? `For workflow-driven cases, reminders may be sent <strong>${escapeHtml(String(p.reminderDays ?? "—"))}</strong> day(s) before the expected completion of probation where that rule applies.`
      : ""
  }</p>
  <p>${
    p.approvalMethod === "workflow"
      ? `The organization limits how many times probation may be extended under this policy to <strong>${escapeHtml(
          String(p.limitProbationExtensions ?? "—")
        )}</strong> extension episode(s), where extension is used.`
      : ""
  }</p>
  ${wfStepsHtml(wf, "Confirmation and review workflow")}
  ${
    cancelWf.length
      ? wfStepsHtml(
          cancelWf,
          "Cancellation or withdrawal of the confirmation process"
        )
      : ""
  }
  <p>Where parallel approvers are used, multiple roles may act as defined for this policy. Escalation may apply if no action is taken within <strong>${escapeHtml(
    String(p.workflowEscalationAfterDays ?? 3)
  )}</strong> day(s). ${
    yn(p.workflowAutoApprovalIfNoAction)
      ? "Where this policy allows it, long-pending steps may be closed automatically in line with the rules set out herein."
      : "Pending steps are not closed automatically without human action unless HR decides otherwise."
  }</p>
  <p>${
    yn(p.workflowRejectionWithReason !== false)
      ? "If a request is rejected, a <strong>reason</strong> should be recorded so the employee and HR understand the basis."
      : "Rejection reasons may still be recorded as per practice."
  } ${
    yn(p.workflowReinitiationAllowed !== false)
      ? "A fresh confirmation request may be allowed after rejection, as per HR guidance."
      : "Re-initiation after rejection may be restricted; employees should contact HR."
  }</p>

  <h2>${docNum.major()}. Extension of probation</h2>
  <p>An extension is not an automatic right; it is an exception to the standard probation end date and must be justified by business need, documented performance discussion, and approvals. Employees should not assume an extension will be granted.</p>
  ${
    extAllowed === false
      ? `<p><strong>${companyName}</strong> has chosen <strong>not</strong> to allow probation extension under this policy. Any exception would require a separate management-approved route.</p>`
      : `<p>Extension is <strong>allowed</strong>. An employee may receive up to <strong>${escapeHtml(
          String(p.maxExtensions ?? "—")
        )}</strong> extension episode(s) under this policy. Each extension episode has a duration of ${
          p.extensionDurationType === "fixed"
            ? `<strong>${escapeHtml(String(p.extensionDurationDays ?? 90))} day(s)</strong> where a fixed duration applies.`
            : `<strong>custom duration per request</strong>, subject to approval and documentation.`
        }</p>
        <p>${
          yn(p.extensionReasonMandatory === "yes")
            ? "A <strong>documented reason</strong> is required for every extension request."
            : "A reason may be requested for transparency even where not mandatory."
        }</p>
        <p>${
          yn(p.performanceReviewMandatoryBeforeExtension === "yes")
            ? "A performance review must be completed <strong>before</strong> an extension is approved."
            : "A performance review before extension is recommended unless HR specifies otherwise."
        }</p>
        <p>${
          yn(p.finalExtensionAutoTriggerTermination === "yes")
            ? "If this is the <strong>last permitted extension</strong> and outcomes are still unsatisfactory, the process may <strong>lead to</strong> termination-related handling, subject to management approval and the terms of this policy."
            : "Final extension does not, by itself, trigger automatic termination under this policy; management and HR will decide the next steps."
        }</p>
        ${wfStepsHtml(extWf, "Extension approval workflow")}`
  }

  <h2>${docNum.major()}. Termination during probation and separation</h2>
  <p>Separation during probation, where allowed, must follow fair process, the approval chain set out below, and applicable notice and payment rules. Nothing in this section reduces any statutory protection or contractual right that applies to the employee.</p>
  ${
    termAllowed === false
      ? `<p>Termination during probation under <em>this</em> policy path is <strong>disabled</strong>. Other company policies or statutory processes may still apply.</p>`
      : `<p>Termination during probation is <strong>permitted</strong> under this policy, subject to due process and approvals. Notice of <strong>${escapeHtml(
          String(p.terminationNoticePeriodDays ?? 0)
        )}</strong> day(s) applies as stated in this policy (or as required by law or contract, whichever is higher).</p>
        <p>${
          yn(p.terminationMultiLevelApproval === "yes")
            ? "Multiple approval levels are required before a termination decision is finalized."
            : "Approval levels follow the workflow or single-approver rules described in this policy."
        }</p>
        <p>${
          yn(p.experienceLetterAllowed === "yes")
            ? `Where separation occurs, an experience or service certificate may be issued as per <strong>${companyName}</strong> practice, subject to clearance.`
            : `Experience letter issuance may be restricted; employees should confirm with HR.`
        }</p>
        <p>Full and Final settlement of dues will be triggered on an <strong>${
          p.fullAndFinalSettlementTrigger === "auto" ? "automatic" : "manual"
        }</strong> basis after separation steps are completed.</p>
        ${wfStepsHtml(termWf, "Termination approval workflow")}`
  }

  ${renderChecklistSection()}
  <h2>${docNum.major()}. Letters, templates, and employee communication</h2>
  <p>${
    yn(p.autoLetter === "yes")
      ? `Confirmation letters may be issued <strong>automatically</strong> when eligibility criteria under this policy are met.`
      : `Confirmation letters are not issued automatically; HR will issue communications as appropriate.`
  }</p>
  <p>Where templates are selected: confirmation letters may follow <strong>${escapeHtml(
    letterLabel(options.letterFormatOptions, p.letterConfirmation)
  )}</strong>; extension communications <strong>${escapeHtml(letterLabel(options.letterFormatOptions, p.letterExtension))}</strong>; and termination-related letters <strong>${escapeHtml(
    letterLabel(options.letterFormatOptions, p.letterTermination)
  )}</strong>. General letter format (if used): <strong>${escapeHtml(letterLabel(options.letterFormatOptions, p.letterFormat))}</strong>.</p>
  <p>Employees will receive communications through official channels (email, employee portal, or physical letter as per practice). Please read any letter carefully; it may contain role-specific terms in addition to this policy summary.</p>

  <h2>${docNum.major()}. Disclaimer</h2>
  <p>This document is an employee-facing summary of the confirmation and probation rules approved for <strong>${companyName}</strong>. It explains how processes are intended to operate; it is not a contract by itself. Your employment contract, appointment letter, applicable statutes, and any collective or company-wide instruments may contain additional or different provisions — where there is a conflict, those documents and the law prevail.</p>
  <p>${companyName} may change this policy from time to time. When a new version applies (see the version and effective date on the first page), HR will communicate material changes through appropriate channels. If you have questions about how this policy applies to your case, please contact HR.</p>

  <div class="sign">
    <div>
      <div class="line">Prepared by (${escapeHtml(preparedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
    <div>
      <div class="line">Approved by (${escapeHtml(approvedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
  </div>
</body>
</html>`;
}

export function printConfirmationPolicyDocumentHtml(htmlString) {
  const w = window.open("", "_blank");
  if (!w) {
    window.print();
    return;
  }
  w.document.write(htmlString);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 300);
}
